import React, { useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  ScanLine, Search, Loader2, Camera, CameraOff, X, CheckCircle2,
  XCircle, Users, Award, Activity, ArrowRight, Calendar, Plus,
} from "lucide-react";
import { inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { adminUrl } from "@/lib/adminPath";

/**
 * Admin · QR Check-In
 *
 * Reception flow:
 *   1. Click "Start Scanner" → open camera and scan the QR on a printed NPC card
 *      (encoded URL `https://<domain>/verify/<member_id>`) OR paste a
 *      membership number / member id / mobile.
 *   2. Backend resolves via /api/admin/verify-member (existing endpoint, reused).
 *   3. Member summary card renders: name, plan, status, family, last visit,
 *      benefit summary, and a "Create Visit" CTA → /admin/visits?member_id=...
 *      which prefills the New Visit modal.
 *
 * Roles: staff + admin + super_admin (read-only for accounts via the
 * /verify-member endpoint; but accounts can still benefit from the lookup).
 */
export default function AdminCheckin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canWriteVisits = ["super_admin", "admin", "staff"].includes(user?.role);

  // Search input (URL OR member id OR membership number OR mobile)
  const [query, setQuery] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {found, membership, matched_member, ...}
  const [notFoundFor, setNotFoundFor] = useState("");

  // Track today's analytics
  const [stats, setStats] = useState({ today: 0, week: 0 });

  // QR camera lifecycle
  const scannerRef = useRef(null);
  const scannerElementId = "npc-qr-scanner-region";

  // Load today/week check-in counts (server-side)
  const loadStats = async () => {
    try {
      const today = new Date();
      const from = today.toISOString().slice(0, 10);
      const r1 = await api.get("/visits", { params: { from_date: from, limit: 1, page: 1 } });
      const startWeek = new Date(today);
      startWeek.setDate(today.getDate() - 7);
      const r2 = await api.get("/visits", {
        params: { from_date: startWeek.toISOString().slice(0, 10), limit: 1, page: 1 },
      });
      setStats({ today: r1.data?.total ?? 0, week: r2.data?.total ?? 0 });
    } catch { /* non-blocking */ }
  };
  useEffect(() => { loadStats(); }, []);

  const lookup = async (raw) => {
    const q = (raw || "").trim();
    if (!q) return;
    setBusy(true);
    setNotFoundFor("");
    try {
      const r = await api.get("/admin/verify-member", {
        params: { q, method: scanning ? "qr_scan" : "manual_search" },
      });
      if (r.data?.found) {
        setResult(r.data);
        setNotFoundFor("");
      } else {
        setResult(null);
        setNotFoundFor(q);
      }
    } catch (e) {
      toast.error(apiErr(e, "Lookup failed"));
    } finally {
      setBusy(false);
    }
  };

  // QR scanner — start / stop
  const startScanner = async () => {
    setResult(null);
    setNotFoundFor("");
    setScanning(true);
    // Slight delay to let the scanner region render
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode(scannerElementId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decoded) => {
            // Stop FIRST so we don't double-trigger
            try { await scanner.stop(); } catch { /* ignore */ }
            try { await scanner.clear(); } catch { /* ignore */ }
            scannerRef.current = null;
            setScanning(false);
            setQuery(decoded);
            lookup(decoded);
          },
          () => { /* per-frame failure callback intentionally empty */ },
        );
      } catch (e) {
        setScanning(false);
        const msg = (e?.message || "").toLowerCase();
        if (msg.includes("permission") || msg.includes("denied") || msg.includes("notallowed")) {
          toast.error("Camera permission denied — please allow camera access or use Manual Lookup below.");
        } else {
          toast.error("Camera unavailable on this device. Use Manual Lookup below.");
        }
      }
    }, 100);
  };

  const stopScanner = async () => {
    try { await scannerRef.current?.stop(); } catch { /* ignore */ }
    try { await scannerRef.current?.clear(); } catch { /* ignore */ }
    scannerRef.current = null;
    setScanning(false);
  };

  // Cleanup if user navigates away
  useEffect(() => () => { stopScanner(); }, []);

  const goCreateVisit = (memberId) => {
    nav(adminUrl(`/visits?member_id=${memberId}`));
  };

  return (
    <div data-testid="admin-checkin-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0F2540] flex items-center gap-2">
            <ScanLine className="h-6 w-6 text-[#1E4F87]" />
            QR Check-In
          </h1>
          <p className="text-sm text-[#5C6B7A] mt-1">Scan a member&apos;s NPC card or search by membership number to start a new visit.</p>
        </div>
        <div className="flex items-center gap-3">
          <CountTile label="Visits today" value={stats.today} icon={Calendar} />
          <CountTile label="Last 7 days" value={stats.week} icon={Activity} tone="gold" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT — Scanner + Search */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-[#E1E8F0] bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-[#0F2540] flex items-center gap-2">
                <Camera className="h-4 w-4" /> Camera Scanner
              </div>
              {scanning ? (
                <button onClick={stopScanner} data-testid="checkin-stop-scanner"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#A53A2A] text-white rounded-xl text-xs font-semibold hover:bg-[#8A2F22]">
                  <CameraOff className="h-3.5 w-3.5" /> Stop
                </button>
              ) : (
                <button onClick={startScanner} data-testid="checkin-start-scanner"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1E4F87] text-white rounded-xl text-xs font-semibold hover:bg-[#0F2540]">
                  <Camera className="h-3.5 w-3.5" /> Start scanner
                </button>
              )}
            </div>
            <div
              id={scannerElementId}
              className={`rounded-xl overflow-hidden bg-[#0F2540] ${scanning ? "min-h-[260px]" : "min-h-[160px] flex items-center justify-center"}`}
            >
              {!scanning && (
                <div className="text-center text-[#EAF1FA] py-8">
                  <ScanLine className="h-10 w-10 opacity-70 mx-auto mb-2" />
                  <p className="text-xs opacity-80">Camera idle. Click <strong>Start scanner</strong> to scan a printed NPC card.</p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-[#E1E8F0] bg-white p-5">
            <div className="text-sm font-semibold text-[#0F2540] flex items-center gap-2 mb-3">
              <Search className="h-4 w-4" /> Manual Lookup
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); lookup(query); }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="NPC member id / NPC-M… / mobile / email"
                data-testid="checkin-search-input"
                autoFocus
                className="flex-1 px-3 py-2 border border-[#E1E8F0] rounded-xl text-sm focus:border-[#1E4F87] outline-none"
              />
              <button type="submit" disabled={busy || !query.trim()} data-testid="checkin-search-btn"
                className="px-4 py-2 bg-[#1E4F87] text-white rounded-xl text-sm font-semibold hover:bg-[#0F2540] disabled:opacity-50 inline-flex items-center gap-1.5">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Lookup
              </button>
            </form>
            <p className="text-xs text-[#5C6B7A] mt-2">
              Accepts membership numbers (NPC-M…), member IDs (NPC…), 10-digit mobile, email, or a scanned <code>/verify/…</code> URL.
            </p>
          </div>
        </div>

        {/* RIGHT — Result */}
        <div className="lg:col-span-3">
          {notFoundFor && (
            <div className="rounded-2xl border border-[#FCDADA] bg-[#FFF4F2] p-5 flex items-start gap-3" data-testid="checkin-not-found">
              <XCircle className="h-5 w-5 text-[#A53A2A] mt-0.5" />
              <div>
                <div className="font-semibold text-[#0F2540]">No member matched</div>
                <p className="text-sm text-[#5C6B7A] mt-0.5">
                  Could not find a membership for <strong className="font-mono">{notFoundFor}</strong>. Double-check the QR or try a different identifier.
                </p>
              </div>
            </div>
          )}

          {result && result.found && (
            <MemberSummaryCard result={result} canWriteVisits={canWriteVisits} onCreateVisit={goCreateVisit} />
          )}

          {!result && !notFoundFor && (
            <div className="rounded-2xl border border-dashed border-[#E1E8F0] bg-white p-8 text-center" data-testid="checkin-idle">
              <Users className="h-10 w-10 text-[#1E4F87] opacity-40 mx-auto mb-2" />
              <p className="text-sm text-[#5C6B7A]">Scan a QR card or run a lookup to load a member here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function MemberSummaryCard({ result, canWriteVisits, onCreateVisit }) {
  const { membership, matched_member, effective_status, is_expired, days_remaining } = result;
  const primary = membership || {};
  const matchedId = matched_member?.member_id || primary.member_id;
  const familyMembers = (primary.members || []).filter((m) => m.member_id !== primary.member_id);

  // Lazy-load wallet + last visit for the matched member
  const [wallet, setWallet] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoadingDetails(true);
    Promise.all([
      api.get(`/members/${matchedId}/benefits`).catch(() => null),
      api.get(`/members/${matchedId}/visit-stats`).catch(() => null),
    ]).then(([w, s]) => {
      if (!alive) return;
      setWallet(w?.data || null);
      setStats(s?.data || null);
    }).finally(() => { if (alive) setLoadingDetails(false); });
    return () => { alive = false; };
  }, [matchedId]);

  const totalBenefits = wallet?.benefits?.length || 0;
  const usedBenefits = useMemo(
    () => (wallet?.benefits || []).filter((b) => (b.used_count || 0) > 0).length,
    [wallet],
  );

  return (
    <div className="rounded-2xl border border-[#E1E8F0] bg-white overflow-hidden" data-testid="checkin-member-card">
      {/* Header strip with status */}
      <div className={`px-5 py-4 flex items-center justify-between ${effective_status === "active" ? "bg-[#E5F2EA]" : "bg-[#FFF4DC]"}`}>
        <div className="flex items-center gap-2">
          {effective_status === "active" ? (
            <CheckCircle2 className="h-5 w-5 text-[#1F7A3D]" />
          ) : (
            <XCircle className="h-5 w-5 text-[#A53A2A]" />
          )}
          <span className={`font-semibold text-sm ${effective_status === "active" ? "text-[#1F7A3D]" : "text-[#A53A2A]"}`}>
            {is_expired ? "Membership expired" : effective_status === "active" ? "Active membership" : "Membership pending"}
          </span>
        </div>
        <span className="text-xs text-[#5C6B7A]">
          {primary.membership_number ? `Membership ${primary.membership_number}` : ""}
          {days_remaining != null && !is_expired && ` · ${days_remaining}d remaining`}
        </span>
      </div>

      <div className="p-5 space-y-4">
        {/* Primary identity */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display text-2xl font-bold text-[#0F2540]" data-testid="checkin-member-name">
              {matched_member?.full_name || primary.full_name}
            </div>
            <div className="text-sm text-[#5C6B7A] mt-1">
              <span className="font-mono">{matchedId}</span> · {primary.plan_name}
              {matched_member?.relation && matched_member.relation !== "Self" && (
                <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-[#EAF1FA] text-[#1E4F87] text-[10px] uppercase tracking-wider">
                  {matched_member.relation}
                </span>
              )}
            </div>
            <div className="text-xs text-[#5C6B7A] mt-1">
              {primary.email} · {primary.mobile}
            </div>
          </div>
          {canWriteVisits && (
            <button
              onClick={() => onCreateVisit(matchedId)}
              data-testid="checkin-create-visit-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1E4F87] text-white rounded-xl text-sm font-semibold hover:bg-[#0F2540]"
            >
              <Plus className="h-4 w-4" /> Create Visit
            </button>
          )}
        </div>

        {/* Quick stats */}
        {loadingDetails ? (
          <div className="py-4 text-center text-xs text-[#5C6B7A]">
            <Loader2 className="h-4 w-4 inline animate-spin" /> Loading benefits…
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SmallStat label="Total Visits" value={stats?.total_visits ?? 0} icon={Activity} />
            <SmallStat label="Last Visit" value={stats?.last_visit_date ? new Date(stats.last_visit_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"} icon={Calendar} />
            <SmallStat label="Total Saved" value={inr(stats?.total_savings ?? 0)} icon={Award} tone="gold" />
            <SmallStat label="Benefits Used" value={`${usedBenefits} / ${totalBenefits}`} icon={Award} />
          </div>
        )}

        {/* Benefit summary */}
        {wallet && (wallet.benefits || []).length > 0 && (
          <div className="rounded-xl bg-[#FAFBFD] border border-[#E1E8F0] p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-[#0F2540]">Available Benefits</div>
              <div className="text-xs text-[#5C6B7A]">Total saved · <span className="font-semibold text-[#1F7A3D]">{inr(wallet.total_savings || 0)}</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {wallet.benefits.slice(0, 6).map((b) => {
                const exhausted = b.total_allowed != null && (b.remaining_count ?? 0) <= 0;
                return (
                  <div key={b.benefit_id} className={`text-xs px-3 py-2 rounded-lg border flex items-center justify-between ${exhausted ? "bg-[#FCE9E6] border-[#FCDADA]" : "bg-white border-[#E1E8F0]"}`}>
                    <div>
                      <div className="font-semibold text-[#0F2540]">{b.title}</div>
                      <div className="text-[10px] text-[#5C6B7A]">{b.category} · {b.reset_period}</div>
                    </div>
                    <div className={`text-xs font-bold ${exhausted ? "text-[#A53A2A]" : "text-[#1F7A3D]"}`}>
                      {b.total_allowed == null ? "Unlimited" : `${b.remaining_count ?? b.total_allowed} left`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Family roster */}
        {familyMembers.length > 0 && (
          <div className="rounded-xl bg-white border border-[#E1E8F0] p-4">
            <div className="text-sm font-semibold text-[#0F2540] mb-2 flex items-center gap-1.5">
              <Users className="h-4 w-4" /> Family ({familyMembers.length})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {familyMembers.map((m) => (
                <button
                  key={m.member_id}
                  onClick={() => canWriteVisits && onCreateVisit(m.member_id)}
                  data-testid={`checkin-family-${m.member_id}`}
                  disabled={!canWriteVisits}
                  className={`text-left p-2.5 rounded-lg border border-[#E1E8F0] hover:border-[#1E4F87] transition flex items-center justify-between ${canWriteVisits ? "cursor-pointer" : "cursor-default opacity-80"}`}
                >
                  <div>
                    <div className="font-semibold text-sm text-[#0F2540]">{m.full_name}</div>
                    <div className="text-xs text-[#5C6B7A]">{m.relation} · <span className="font-mono">{m.member_id}</span></div>
                  </div>
                  {canWriteVisits && <Plus className="h-4 w-4 text-[#1E4F87]" />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function CountTile({ label, value, icon: Icon, tone = "navy" }) {
  const toneCls = tone === "gold" ? "bg-[#FAF1DC] text-[#8C6A00]" : "bg-[#EAF1FA] text-[#1E4F87]";
  return (
    <div className={`rounded-2xl border border-[#E1E8F0] bg-white px-4 py-3 flex items-center gap-3 ${toneCls.includes("FAF1DC") ? "border-[#F0D58A]" : ""}`}>
      <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${toneCls}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#5C6B7A]">{label}</div>
        <div className="font-display text-lg font-bold text-[#0F2540]">{value}</div>
      </div>
    </div>
  );
}

function SmallStat({ label, value, icon: Icon, tone = "navy" }) {
  const toneCls = tone === "gold" ? "bg-[#FAF1DC] text-[#8C6A00] border-[#F0D58A]" : "bg-[#EAF1FA] text-[#1E4F87] border-[#E1E8F0]";
  return (
    <div className={`rounded-xl border p-2.5 flex items-center gap-2 ${toneCls}`}>
      <Icon className="h-4 w-4" />
      <div>
        <div className="text-[9px] uppercase tracking-wider opacity-80">{label}</div>
        <div className="text-sm font-bold text-[#0F2540]">{value}</div>
      </div>
    </div>
  );
}
